import { useMemo } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useQueryWithErrorHandler } from './useQueryWithErrorHandler';
import {
  IResponseData,
  IError,
  IApiParams,
  IPlantGroup,
  INotificationEntityGroup,
  INotificationEntity,
  IAddToNotificationEntity,
  IUpdateNotificationEntityGroup,
  ICreateNotificationEntityGroupRequest,
} from 'types';
import { useAuth, useError } from '../providers';
import { useAuthClient } from './useAuthClient';
import { firebaseAnalytics } from '../analytics';
import { errors } from '../config';

const notificationEntityGroupsKey = 'getNotificationEntityGroups';
const notificationEntityGroupKey = 'getNotificationEntityGroup';
const unassignedNotificationEntity = 'getUnassignedNotificationEntity';

export function useNotificationEntityGroupApi({
  queryParameters,
  organizationId,
  entityType,
}: IApiParams) {
  const authClient = useAuthClient();
  const { openModal } = useError();
  const { supplierId } = useAuth();
  const queryClient = useQueryClient();

  const currentSupplierId = useMemo(() => {
    return organizationId || supplierId;
  }, [organizationId, supplierId]);

  const getUnassignedNotificationEntityUrl = useMemo(() => {
    return `/api/v1/${organizationId}/NotificationEntityGroup/${entityType}/get-unnasigned-notification-entities`;
  }, [entityType, organizationId]);

  const getNotificationEntityGroupsUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/NotificationEntityGroup/${entityType}/get-notification-entities-groups`;
  }, [currentSupplierId, entityType]);

  const getNotificationEntityGroupUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/NotificationEntityGroup/${entityType}/get-notification-entities-group`;
  }, [currentSupplierId, entityType]);

  const createNotificationEntityGroupUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/NotificationEntityGroup/${entityType}/create-notification-entities-group`;
  }, [currentSupplierId, entityType]);

  const removeNotificationEntityFromGroupUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/NotificationEntityGroup/${entityType}/remove-notification-entities-from-group`;
  }, [currentSupplierId, entityType]);

  const addToNotificationEntityGroupUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/NotificationEntityGroup/${entityType}/add-notification-entities-to-group`;
  }, [entityType, currentSupplierId]);

  const updateNotificationEntityGroupUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/NotificationEntityGroup/${entityType}/update-notification-entities-group`;
  }, [currentSupplierId, entityType]);

  const deleteNotificationEntityGroupUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/NotificationEntityGroup/${entityType}/delete-notification-entities-group`;
  }, [currentSupplierId, entityType]);

  const {
    data: notificationEntityGroupsResponse,
    refetch: fetchNotificationEntityGroups,
    isFetching: isFetchingNotificationEntityGroups,
  } = useQueryWithErrorHandler<
    IResponseData<INotificationEntityGroup[]>,
    IError
  >({
    queryKey: [notificationEntityGroupsKey, { queryParameters }],

    queryFn: async () => {
      return await authClient(`${getNotificationEntityGroupsUrl}`, {
        queryParameters,
        method: 'GET',
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,

    onError: error => {
      firebaseAnalytics.logErrorEvent(
        errors.getNotificationEntitiesGroups,
        error,
        {
          getNotificationEntityGroupsUrl,
          ...queryParameters,
        },
      );
      openModal(error);
    },
  });

  const {
    data: notificationEntityGroupResponse,
    refetch: fetchNotificationEntityGroup,
    isFetching: isFetchingNotificationEntityGroup,
  } = useQueryWithErrorHandler<IResponseData<INotificationEntityGroup>, IError>(
    {
      queryKey: [notificationEntityGroupKey, { queryParameters }],

      queryFn: async () => {
        return await authClient(`${getNotificationEntityGroupUrl}`, {
          queryParameters,
          method: 'GET',
        });
      },

      enabled: false,
      refetchOnWindowFocus: false,

      onError: error => {
        firebaseAnalytics.logErrorEvent(
          errors.getNotificationEntitiesGroup,
          error,
          {
            getNotificationEntityGroupUrl,
            ...queryParameters,
          },
        );
        openModal(error);
      },

      placeholderData: keepPreviousData,
    },
  );

  const {
    data: unassignedNotificationEntityResponse,
    refetch: fetchUnassignedNotificationEntity,
    isFetching: isFetchingUnassignedNotificationEntity,
  } = useQueryWithErrorHandler<IResponseData<INotificationEntity[]>, IError>({
    queryKey: [unassignedNotificationEntity, { queryParameters }],

    queryFn: async () => {
      return await authClient(`${getUnassignedNotificationEntityUrl}`, {
        queryParameters,
        method: 'GET',
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,

    onError: error => {
      firebaseAnalytics.logErrorEvent(
        errors.getUnassignedNotificationEntities,
        error,
        {
          getUnassignedNotificationEntityUrl,
          ...queryParameters,
        },
      );
      openModal(error);
    },
  });

  const {
    data: deleteNotificationEntityGroupResponse,
    refetch: deleteNotificationEntityGroup,
    isLoading: isNotificationEntityGroupDeleting,
    error: deleteNotificationEntityGroupError,
  } = useQueryWithErrorHandler<IResponseData<boolean>, IError>({
    queryKey: [notificationEntityGroupKey, { queryParameters }],

    queryFn: async () => {
      return await authClient(`${deleteNotificationEntityGroupUrl}`, {
        queryParameters,
        method: 'DELETE',
      });
    },

    retry: false,
    enabled: false,
    refetchOnWindowFocus: false,

    onError: error => {
      firebaseAnalytics.logErrorEvent(
        errors.deleteNotificationEntitiesGroup,
        error,
        {
          deleteNotificationEntityGroupUrl,
          ...queryParameters,
        },
      );
      openModal(error);
    },
  });

  const {
    mutateAsync: addNotificationEntitiesToGroup,
    isPending: isAddingNotificationEntitiesToGroup,
    isSuccess: isAddingNotificationEntitiesToGroupSuccess,
    error: addingNotificationEntitiesToGroupError,
  } = useMutation({
    mutationFn: (payload: IAddToNotificationEntity) =>
      authClient(addToNotificationEntityGroupUrl, {
        method: 'PUT',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [notificationEntityGroupKey],
      });
      return queryClient.getQueryData<IResponseData<IPlantGroup>>([
        notificationEntityGroupKey,
      ]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<IResponseData<IPlantGroup>>(
          [notificationEntityGroupKey],
          context,
        );
      }

      firebaseAnalytics.logErrorEvent(
        errors.addNotificationEntitiesToGroup,
        error,
        {
          addToNotificationEntityGroupUrl,
          notificationEntityGroupId: variables.notificationEntityGroupId,
          internalIds: variables.internalIds,
        },
      );
      openModal(error);
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [notificationEntityGroupKey],
      });
    },
  });

  const {
    mutateAsync: updateNotificationEntityGroup,
    isPending: isUpdatingNotificationEntityGroup,
    isSuccess: isUpdatingNotificationEntityGroupSuccess,
    error: updatingPlantNotificationEntityError,
  } = useMutation({
    mutationFn: (payload: IUpdateNotificationEntityGroup) =>
      authClient(updateNotificationEntityGroupUrl, {
        method: 'PUT',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [notificationEntityGroupKey],
      });
      return queryClient.getQueryData<IResponseData<IPlantGroup>>([
        notificationEntityGroupKey,
      ]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<IResponseData<IPlantGroup>>(
          [notificationEntityGroupKey],
          context,
        );
      }

      firebaseAnalytics.logErrorEvent(
        errors.updateNotificationEntitiesGroup,
        error,
        {
          updateNotificationEntityGroupUrl,
          notificationEntityGroupId: variables.notificationEntityGroupId,
          notificationEntityGroupName: variables.notificationEntityGroupName,
        },
      );
      openModal(error);
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [notificationEntityGroupKey],
      });
    },
  });

  const {
    mutateAsync: removeNotificationEntityFromGroup,
    isPending: isRemovingNotificationEntityFromGroup,
    isSuccess: isRemovingNotificationEntityFromGroupSuccess,
    error: removingPlantNotificationEntityGroupError,
  } = useMutation({
    mutationFn: (payload: IAddToNotificationEntity) =>
      authClient(removeNotificationEntityFromGroupUrl, {
        method: 'PUT',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [notificationEntityGroupKey],
      });
      return queryClient.getQueryData<IResponseData<IPlantGroup>>([
        notificationEntityGroupKey,
      ]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<IResponseData<IPlantGroup>>(
          [notificationEntityGroupKey],
          context,
        );
      }

      firebaseAnalytics.logErrorEvent(
        errors.removeNotificationEntitiesFromGroup,
        error,
        {
          removeNotificationEntityFromGroupUrl,
          notificationEntityGroupId: variables.notificationEntityGroupId,
          internalIds: variables.internalIds,
        },
      );
      openModal(error);
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [notificationEntityGroupKey],
      });
    },
  });

  const {
    mutateAsync: createNotificationEntityGroup,
    isPending: isNotificationEntityGroupCreating,
    isSuccess: isNotificationEntityGroupSuccess,
    error: createNotificationEntityGroupError,
  } = useMutation({
    mutationFn: (payload: ICreateNotificationEntityGroupRequest) =>
      authClient(createNotificationEntityGroupUrl, {
        method: 'POST',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [notificationEntityGroupKey],
      });
      return queryClient.getQueryData<IResponseData<IPlantGroup>>([
        notificationEntityGroupKey,
      ]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<IResponseData<IPlantGroup>>(
          [notificationEntityGroupKey],
          context,
        );
      }

      firebaseAnalytics.logErrorEvent(
        errors.createNotificationEntitiesGroup,
        error,
        {
          createNotificationEntityGroupUrl,
          notificationEntityGroupName: variables.notificationEntityGroupName,
          internalIds: variables.internalIds,
        },
      );
      openModal(error);
    },
  });

  const unassignedNotificationEntities = useMemo<
    INotificationEntity[] | undefined
  >(
    () => unassignedNotificationEntityResponse?.data,
    [unassignedNotificationEntityResponse?.data],
  );

  const notificationEntityGroups = useMemo<
    INotificationEntityGroup[] | undefined
  >(
    () => notificationEntityGroupsResponse?.data,
    [notificationEntityGroupsResponse],
  );

  const notificationEntityGroup = useMemo<INotificationEntityGroup | undefined>(
    () => notificationEntityGroupResponse?.data,
    [notificationEntityGroupResponse?.data],
  );

  return {
    notificationEntityGroups,
    fetchNotificationEntityGroups,
    isFetchingNotificationEntityGroups,
    fetchNotificationEntityGroup,
    isFetchingNotificationEntityGroup,
    notificationEntityGroup,
    deleteNotificationEntityGroupResponse,
    deleteNotificationEntityGroup,
    isNotificationEntityGroupDeleting,
    deleteNotificationEntityGroupError,
    unassignedNotificationEntities,
    fetchUnassignedNotificationEntity,
    isFetchingUnassignedNotificationEntity,
    addNotificationEntitiesToGroup,
    isAddingNotificationEntitiesToGroup,
    isAddingNotificationEntitiesToGroupSuccess,
    addingNotificationEntitiesToGroupError,
    updateNotificationEntityGroup,
    isUpdatingNotificationEntityGroup,
    isUpdatingNotificationEntityGroupSuccess,
    updatingPlantNotificationEntityError,
    removeNotificationEntityFromGroup,
    isRemovingNotificationEntityFromGroup,
    isRemovingNotificationEntityFromGroupSuccess,
    removingPlantNotificationEntityGroupError,
    createNotificationEntityGroup,
    isNotificationEntityGroupCreating,
    isNotificationEntityGroupSuccess,
    createNotificationEntityGroupError,
  };
}
